/* Meal menu grid — Optimize Meals (Trade 15.4.0)
   - "View details" popup (native <dialog>)
   - Option change (Regular / Protein Plus) updates price + macros on card and popup
   - % Daily Value rings count up from 0 when Macronutrients scrolls into view
   - AJAX add to cart: works with cart type Page, Drawer or Popup notification
*/
(() => {
  if (window.__mealMenuReady) return;
  window.__mealMenuReady = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const RING_MS = 1200;

  /* ---------- Variant data ---------- */
  function getVariants(card) {
    if (!card._variants) {
      const el = card.querySelector('script[data-meal-variants]');
      try {
        card._variants = el ? JSON.parse(el.textContent) : [];
      } catch (e) {
        card._variants = [];
      }
    }
    return card._variants;
  }

  function findVariant(card, id) {
    return getVariants(card).find((v) => String(v.id) === String(id));
  }

  /* Update one scope (a card, or the popup body) to a variant */
  function applyVariant(scope, variant, { animate = true } = {}) {
    if (!scope || !variant) return;
    const form = scope.querySelector('[data-meal-form]');

    if (form) {
      form.querySelector('[data-meal-variant-id]').value = variant.id;

      form.querySelectorAll('input[data-meal-variant]').forEach((r) => {
        r.checked = String(r.value) === String(variant.id);
      });
      const select = form.querySelector('select[data-meal-variant]');
      if (select) select.value = String(variant.id);

      const price = form.querySelector('[data-meal-price]');
      if (price && variant.price) price.textContent = variant.price;

      const btn = form.querySelector('[data-meal-add]');
      if (btn && btn.getAttribute('aria-busy') !== 'true') {
        btn.disabled = !variant.available;
        btn.textContent = variant.available ? btn.dataset.labelAdd : btn.dataset.labelSold;
      }
      hideError(form);
    }

    scope.querySelectorAll('[data-macro]').forEach((el) => {
      const val = variant[el.dataset.macro];
      if (val !== null && val !== undefined && val !== '') el.textContent = Math.round(Number(val));
    });

    // Ingredients + allergens can differ per option (popup only)
    const ing = scope.querySelector('[data-meal-ingredients]');
    if (ing) {
      ing.textContent = variant.ing || '';
      ing.closest('[data-meal-ing-block]').hidden = !variant.ing;
    }
    const al = scope.querySelector('[data-meal-allergens]');
    if (al) {
      al.textContent = variant.al || '';
      al.closest('[data-meal-al-block]').hidden = !variant.al;
    }

    if (form) paintForm(form);

    // Nutrition Facts label (one Shopify Page per option)
    const labels = scope.querySelectorAll('[data-meal-label]');
    if (labels.length) {
      let found = false;
      labels.forEach((l) => {
        const on = l.dataset.mealLabel === String(variant.id);
        l.hidden = !on;
        if (on) found = true;
      });
      const empty = scope.querySelector('[data-meal-label-empty]');
      if (empty) empty.hidden = found;
    }

    scope.querySelectorAll('[data-ring]').forEach((ring) => {
      const val = Number(variant[ring.dataset.ring]);
      const dv = Number(ring.dataset.dv);
      if (!dv || Number.isNaN(val)) return;
      const target = Math.round((val * 100) / dv);
      ring.dataset.target = target;
      const panel = ring.closest('[data-meal-nutrition]');
      // Only move the ring if its panel has already been revealed
      if (panel && panel.dataset.revealed === 'true') animateRing(ring, target, animate);
    });
  }

  /* ---------- Rings ---------- */
  function setRing(ring, pct) {
    const arc = ring.querySelector('[data-ring-arc]');
    const label = ring.querySelector('[data-ring-pct]');
    if (arc) arc.setAttribute('stroke-dashoffset', String(100 - Math.min(Math.max(pct, 0), 100)));
    if (label) label.textContent = Math.round(pct);
    ring._value = pct;
  }

  function animateRing(ring, to, animate = true) {
    cancelAnimationFrame(ring._raf);
    const from = ring._value || 0;
    if (!animate || reduceMotion.matches || from === to) {
      setRing(ring, to);
      return;
    }
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min((now - start) / RING_MS, 1);
      setRing(ring, from + (to - from) * ease(t));
      if (t < 1) ring._raf = requestAnimationFrame(tick);
      else setRing(ring, to);
    };
    ring._raf = requestAnimationFrame(tick);
  }

  function prepareRings(body) {
    const panel = body.querySelector('[data-meal-nutrition]');
    if (!panel) return;
    panel.dataset.revealed = 'false';
    const rings = panel.querySelectorAll('[data-ring]');
    rings.forEach((ring) => setRing(ring, 0));

    const reveal = () => {
      if (panel.dataset.revealed === 'true') return;
      panel.dataset.revealed = 'true';
      rings.forEach((ring) => animateRing(ring, Number(ring.dataset.target) || 0, true));
    };

    if (!('IntersectionObserver' in window)) {
      reveal();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          reveal();
          io.disconnect();
        }
      },
      { root: body, threshold: 0.35 }
    );
    io.observe(panel);
    body._io = io;
  }

  function sectionHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /* ---------- Build-a-box mode: live Shopify cart ---------- */
  const boxMode = () => !!document.querySelector('[data-meal-progress]');
  const cartState = { qty: {}, count: 0, total: 0, currency: 'USD', pageIds: null };

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: cartState.currency,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  // Variant ids of the meals on this page (all à la carte) — used when the theme status section is missing
  function pageVariantIds() {
    if (!cartState.pageIds) {
      cartState.pageIds = new Set();
      document.querySelectorAll('script[data-meal-variants]').forEach((el) => {
        try {
          JSON.parse(el.textContent).forEach((v) => cartState.pageIds.add(String(v.id)));
        } catch (e) {}
      });
    }
    return cartState.pageIds;
  }

  function parseStatus(html) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ala-status]');
  }

  /* Only à la carte meals count toward the bar — never meal plans or other products.
     1st choice: the theme's "ala-cart-status" section (checks the product tag).
     Fallback: count only cart lines that are one of the meals shown on this page. */
  function readCart(cart, statusHtml) {
    cartState.qty = {};
    (cart.items || []).forEach((item) => {
      cartState.qty[item.variant_id] = (cartState.qty[item.variant_id] || 0) + item.quantity;
    });
    if (cart.currency) cartState.currency = cart.currency;

    const ids = pageVariantIds();
    let pageCount = 0;
    let pageTotal = 0;
    (cart.items || []).forEach((item) => {
      if (ids.has(String(item.variant_id))) {
        pageCount += item.quantity;
        pageTotal += item.final_line_price != null ? item.final_line_price : item.line_price || 0;
      }
    });

    const status = parseStatus(statusHtml || (cart.sections && cart.sections['ala-cart-status']));
    if (status && status.dataset.count !== undefined) {
      cartState.count = parseInt(status.dataset.count, 10) || 0;
      cartState.total = status.dataset.total !== undefined ? parseInt(status.dataset.total, 10) || 0 : pageTotal;
    } else {
      cartState.count = pageCount;
      cartState.total = pageTotal;
    }
    paintBox();
  }

  function paintForm(form) {
    const idInput = form.querySelector('[data-meal-variant-id]');
    const stepper = form.querySelector('[data-meal-box-qty]');
    if (!idInput || !stepper) return;
    const qty = cartState.qty[idInput.value] || 0;
    const btn = form.querySelector('[data-meal-add]');
    stepper.hidden = qty === 0;
    if (btn) btn.hidden = qty > 0;
    const count = stepper.querySelector('[data-meal-box-count]');
    if (count) count.textContent = qty;
    const card = form.closest('[data-meal-card]');
    if (card) card.classList.toggle('is-in-box', qty > 0);
  }

  function paintBox() {
    document.querySelectorAll('[data-meal-form]').forEach(paintForm);

    document.querySelectorAll('[data-meal-progress]').forEach((bar) => {
      const min = parseInt(bar.dataset.min, 10) || 0;
      const count = cartState.count || 0;
      const left = Math.max(min - count, 0);
      const fill = (el, text) => { if (el) el.textContent = text; };
      const swap = (t) => (t || '')
        .replace(/\[count\]/g, count)
        .replace(/\[min\]/g, min)
        .replace(/\[left\]/g, left);

      fill(bar.querySelector('[data-meal-progress-count]'), swap(bar.dataset.textCount));
      fill(bar.querySelector('[data-meal-progress-msg]'), left > 0 ? swap(bar.dataset.textLeft) : swap(bar.dataset.textDone));
      fill(bar.querySelector('[data-meal-progress-total]'), formatMoney(cartState.total));

      const track = bar.querySelector('[data-meal-progress-fill]');
      if (track) track.style.width = (min ? Math.min(count / min, 1) : 1) * 100 + '%';

      const button = bar.querySelector('[data-meal-progress-button]');
      if (button) {
        button.textContent = left > 0 ? swap(bar.dataset.labelLeft) : swap(bar.dataset.labelGo);
        button.setAttribute('aria-disabled', left > 0 ? 'true' : 'false');
      }
      bar.classList.toggle('is-done', left === 0);
      bar.classList.toggle('is-empty', count === 0);
    });
  }

  async function setQty(variantId, qty, form) {
    const body = { updates: { [variantId]: Math.max(0, qty) }, sections: 'cart-icon-bubble,ala-cart-status', sections_url: window.location.pathname };
    if (form) {
      const btn = form.querySelector('[data-meal-add]');
      if (btn) btn.setAttribute('aria-busy', 'true');
      hideError(form);
    }
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const cart = await res.json();
      if (cart.status && cart.status !== 200) {
        if (form) showError(form, cart.description || cart.message || 'Could not update your box.');
        return;
      }
      readCart(cart);
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && cart.sections && cart.sections['cart-icon-bubble']) {
        bubble.innerHTML = sectionHTML(cart.sections['cart-icon-bubble']);
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'meal-menu', productVariantId: variantId, cartData: cart });
      }
    } catch (err) {
      console.error(err);
      if (form) showError(form, 'Could not update your box. Check your connection and try again.');
    } finally {
      if (form) {
        const btn = form.querySelector('[data-meal-add]');
        if (btn) btn.removeAttribute('aria-busy');
      }
    }
  }

  function currentQty(form) {
    const id = form.querySelector('[data-meal-variant-id]').value;
    return { id, qty: cartState.qty[id] || 0 };
  }

  if (boxMode()) {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    Promise.all([
      fetch(cartUrl + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((r) => r.json()),
      fetch(cartUrl + '?section_id=ala-cart-status', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((r) => (r.ok ? r.text() : ''))
        .catch(() => ''),
    ])
      .then(([cart, status]) => readCart(cart, status))
      .catch(() => paintBox());
  }

  /* ---------- Popup ---------- */
  function openDetails(card) {
    const root = card.closest('[data-meal-menu]');
    const dialog = root && root.querySelector('[data-meal-modal]');
    const tpl = card.querySelector('template[data-meal-detail]');
    if (!dialog || !tpl) return false;

    const body = dialog.querySelector('[data-meal-modal-body]');
    if (body._io) body._io.disconnect();
    body.replaceChildren(tpl.content.cloneNode(true));
    dialog.querySelector('[data-meal-modal-name]').textContent = card.dataset.title || '';
    dialog._card = card;
    dialog._opener = document.activeElement;

    // Start the popup on the option chosen on the card
    const cardId = card.querySelector('[data-meal-variant-id]');
    const variant = cardId && findVariant(card, cardId.value);
    if (variant) applyVariant(body, variant, { animate: false });

    prepareRings(body);

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    body.scrollTop = 0;
    return true;
  }

  function closeDetails(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  document.addEventListener(
    'close',
    (e) => {
      const dialog = e.target;
      if (!dialog.matches || !dialog.matches('[data-meal-modal]')) return;
      const body = dialog.querySelector('[data-meal-modal-body]');
      if (body._io) body._io.disconnect();
      body.replaceChildren();
      const opener = dialog._opener;
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    },
    true
  );

  /* ---------- Clicks ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-meal-open]');
    if (opener) {
      if (opener.tagName === 'A' && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) return;
      const card = opener.closest('[data-meal-card]');
      if (card && openDetails(card)) e.preventDefault();
      return;
    }

    const closer = e.target.closest('[data-meal-close]');
    if (closer) {
      closeDetails(closer.closest('[data-meal-modal]'));
      return;
    }

    // Backdrop click
    if (e.target.matches && e.target.matches('[data-meal-modal][open]')) {
      const r = e.target.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) closeDetails(e.target);
      return;
    }

    const boxStep = e.target.closest('[data-meal-box-step]');
    if (boxStep) {
      const form = boxStep.closest('[data-meal-form]');
      const { id, qty } = currentQty(form);
      setQty(id, qty + parseInt(boxStep.dataset.mealBoxStep, 10), form);
      return;
    }

    const goButton = e.target.closest('[data-meal-progress-button]');
    if (goButton && goButton.getAttribute('aria-disabled') === 'true') {
      e.preventDefault();
      return;
    }

    const step = e.target.closest('[data-meal-qty]');
    if (step) {
      const input = step.parentElement.querySelector('[data-meal-qty-input]');
      const current = parseInt(input.value, 10) || 1;
      input.value = step.dataset.mealQty === 'plus' ? current + 1 : Math.max(1, current - 1);
    }
  });

  /* ---------- Changes ---------- */
  document.addEventListener('change', (e) => {
    const qty = e.target.closest('[data-meal-qty-input]');
    if (qty) {
      const n = parseInt(qty.value, 10);
      if (!n || n < 1) qty.value = 1;
      return;
    }

    const picker = e.target.closest('[data-meal-variant]');
    if (!picker) return;

    const dialog = picker.closest('[data-meal-modal]');
    const card = dialog ? dialog._card : picker.closest('[data-meal-card]');
    if (!card) return;

    const variant = findVariant(card, picker.value);
    if (!variant) return;

    if (dialog) {
      applyVariant(dialog.querySelector('[data-meal-modal-body]'), variant);
      applyVariant(card, variant, { animate: false }); // keep the card in sync
    } else {
      applyVariant(card, variant);
    }
  });

  /* ---------- Add to cart ---------- */
  function showError(form, message) {
    const el = form.querySelector('[data-meal-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }
  function hideError(form) {
    const el = form.querySelector('[data-meal-error]');
    if (el) el.hidden = true;
  }

  function showToast(root, message) {
    const toast = root && root.querySelector('[data-meal-toast]');
    if (!toast) return;
    toast.querySelector('[data-meal-toast-text]').textContent = message;
    toast.hidden = false;
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => (toast.hidden = true), 4000);
  }

  function flashAdded(btn) {
    btn.textContent = btn.dataset.labelAdded || 'Added';
    clearTimeout(btn._timer);
    btn._timer = setTimeout(() => {
      btn.textContent = btn.disabled ? btn.dataset.labelSold : btn.dataset.labelAdd;
    }, 1800);
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-meal-form]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[data-meal-add]');
    if (btn.getAttribute('aria-busy') === 'true' || btn.disabled) return;

    if (boxMode()) {
      const { id, qty } = currentQty(form);
      setQty(id, qty + 1, form);
      return;
    }

    const root = form.closest('[data-meal-menu]');
    const dialog = form.closest('[data-meal-modal]');
    const card = dialog ? dialog._card : form.closest('[data-meal-card]');
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const useThemeCart = cart && typeof cart.renderContents === 'function';

    const data = new FormData(form);
    const qty = data.get('quantity') || '1';
    const body = new FormData();
    body.append('id', data.get('id'));
    body.append('quantity', qty);
    body.append('sections', useThemeCart ? cart.getSectionsToRender().map((s) => s.id) : 'cart-icon-bubble');
    body.append('sections_url', window.location.pathname);

    btn.setAttribute('aria-busy', 'true');
    hideError(form);
    if (useThemeCart && typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);

    try {
      const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body,
      });
      const response = await res.json();

      if (response.status) {
        showError(form, response.description || response.message || 'This meal could not be added. Try again.');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, { source: 'meal-menu', productVariantId: data.get('id'), errors: response.description, message: response.message });
        }
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'meal-menu', productVariantId: data.get('id'), cartData: response });
      }

      if (useThemeCart) {
        if (dialog && dialog.open) closeDetails(dialog);
        cart.renderContents(response);
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      } else {
        // Cart type = Page: stay on the menu, refresh the cart count, confirm
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && response.sections && response.sections['cart-icon-bubble']) {
          bubble.innerHTML = sectionHTML(response.sections['cart-icon-bubble']);
        }
        if (dialog && dialog.open) closeDetails(dialog);
        const cardBtn = card && card.querySelector('[data-meal-add]');
        if (cardBtn) flashAdded(cardBtn);
        const title = (card && card.dataset.title) || response.product_title || 'Meal';
        const option = response.variant_title ? ` (${response.variant_title})` : '';
        showToast(root, `${qty} \u00d7 ${title}${option} added to your cart`);
      }

      const qtyInput = form.querySelector('[data-meal-qty-input]');
      if (qtyInput) qtyInput.value = 1;
    } catch (err) {
      console.error(err);
      showError(form, 'This meal could not be added. Check your connection and try again.');
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });
})();
